import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import { GameState } from '../systems/GameState';
import { EventBus } from '../systems/EventBus';
import { AudioManager } from '../systems/AudioManager';
import { Theme } from '../ui/theme';

// Chip x-positions (exposed so other scenes can target them for fly-to animation)
export const HUD_CHIP_X = { stars: 280, coins: 460, gems: 640 };
export const HUD_CHIP_Y = 32;

export class HUDScene extends Phaser.Scene {
  private starsText!: Phaser.GameObjects.Text;
  private coinsText!: Phaser.GameObjects.Text;
  private gemsText!: Phaser.GameObjects.Text;
  private muteBtn!: Phaser.GameObjects.Text;
  private muteBg!: Phaser.GameObjects.Rectangle;

  // Previous values to detect changes for animation
  private prevStars = 0;
  private prevCoins = 0;
  private prevGems = 0;

  private currencyFlyHandler = (data: { type: 'stars' | 'coins' | 'gems'; amount: number; fromX: number; fromY: number }) => {
    this._flyToChip(data.type, data.amount, data.fromX, data.fromY);
  };

  constructor() { super({ key: 'HUDScene', active: false }); }

  create(): void {
    // 상단 흰 바 (subtle bottom shadow)
    const barG = this.add.graphics();
    barG.fillStyle(0xffffff, 1);
    barG.fillRect(0, 0, GAME_WIDTH, 60);
    barG.fillStyle(0x3a2540, 0.06);
    barG.fillRect(0, 59, GAME_WIDTH, 4);

    // 타이틀
    this.add.text(40, 32, "Roha's Closet", {
      fontFamily: Theme.font.family,
      fontSize: '20px',
      fontStyle: '800',
      color: Theme.hex.pink,
    }).setOrigin(0, 0.5);

    // 통화 칩들
    this.starsText = this.makeChip(HUD_CHIP_X.stars, '⭐', GameState.data.currency.stars, 0xfff5f8);
    this.coinsText = this.makeChip(HUD_CHIP_X.coins, '💰', GameState.data.currency.coins, 0xfff8e0);
    this.gemsText  = this.makeChip(HUD_CHIP_X.gems,  '💎', GameState.data.currency.gems,  0xf0eaff);

    this.prevStars = GameState.data.currency.stars;
    this.prevCoins = GameState.data.currency.coins;
    this.prevGems  = GameState.data.currency.gems;

    // 이벤트 구독
    EventBus.on('currency-changed', this.refresh, this);
    EventBus.on('currency-fly', this.currencyFlyHandler);
    this.events.once('shutdown', () => {
      EventBus.off('currency-changed', this.refresh, this);
      EventBus.off('currency-fly', this.currencyFlyHandler);
    });

    // 퀘스트 버튼 (pill chip)
    const questG = this.add.graphics();
    questG.fillStyle(0xfff5f8, 1);
    questG.fillRoundedRect(GAME_WIDTH - 172, 12, 68, 40, 10);
    questG.lineStyle(1.5, Theme.colors.pink.light, 1);
    questG.strokeRoundedRect(GAME_WIDTH - 172, 12, 68, 40, 10);
    this.add.text(GAME_WIDTH - 138, 32, '🎯', { fontSize: '22px' }).setOrigin(0.5);
    const questHit = this.add.rectangle(GAME_WIDTH - 138, 32, 68, 40, 0, 0);
    questHit.setInteractive({ useHandCursor: true });
    questHit.on('pointerdown', () => { this.scene.launch('QuestPanelScene'); });

    // 🔊 뮤트 버튼
    const muted = GameState.data.settings.muted;
    const muteG = this.add.graphics();
    muteG.fillStyle(0xfff5f8, 1);
    muteG.fillRoundedRect(GAME_WIDTH - 94, 12, 80, 40, 10);
    muteG.lineStyle(1.5, Theme.colors.pink.light, 1);
    muteG.strokeRoundedRect(GAME_WIDTH - 94, 12, 80, 40, 10);
    this.muteBg = this.add.rectangle(GAME_WIDTH - 54, 32, 80, 40, 0, 0);
    this.muteBg.setInteractive({ useHandCursor: true });
    this.muteBtn = this.add.text(GAME_WIDTH - 54, 32, muted ? '🔇' : '🔊', { fontSize: '22px' }).setOrigin(0.5);
    this.muteBg.on('pointerdown', () => this._toggleMute());
  }

  private _toggleMute(): void {
    const nowMuted = !GameState.data.settings.muted;
    AudioManager.setMuted(nowMuted);
    this.muteBtn.setText(nowMuted ? '🔇' : '🔊');
    this.tweens.add({
      targets: this.muteBg,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 80,
      yoyo: true,
      ease: 'Back.Out',
    });
  }

  private makeChip(x: number, emoji: string, value: number, bg: number): Phaser.GameObjects.Text {
    const chipW = 148, chipH = 38;
    const g = this.add.graphics();
    g.fillStyle(bg, 1);
    g.fillRoundedRect(x - chipW / 2, 32 - chipH / 2, chipW, chipH, 10);
    g.lineStyle(1.5, Theme.colors.pink.light, 0.8);
    g.strokeRoundedRect(x - chipW / 2, 32 - chipH / 2, chipW, chipH, 10);
    this.add.text(x - chipW / 2 + 20, 32, emoji, { fontSize: '18px' }).setOrigin(0.5);
    return this.add.text(x + 14, 32, value.toLocaleString(), {
      fontFamily: Theme.font.family,
      fontSize: '15px',
      fontStyle: '700',
      color: Theme.hex.text,
    }).setOrigin(0.5);
  }

  private _getChipTarget(type: 'stars' | 'coins' | 'gems'): { x: number; text: Phaser.GameObjects.Text } {
    if (type === 'stars') return { x: HUD_CHIP_X.stars, text: this.starsText };
    if (type === 'coins') return { x: HUD_CHIP_X.coins, text: this.coinsText };
    return { x: HUD_CHIP_X.gems, text: this.gemsText };
  }

  /** Fly +N currency label from (fromX,fromY) to chip in HUD, then bounce chip */
  private _flyToChip(type: 'stars' | 'coins' | 'gems', amount: number, fromX: number, fromY: number): void {
    const emoji = type === 'stars' ? '⭐' : type === 'coins' ? '💰' : '💎';
    const color = type === 'stars' ? '#ffd700' : type === 'coins' ? '#f0a010' : '#9b6bff';
    const { x: chipX, text: chipText } = this._getChipTarget(type);

    const label = this.add.text(fromX, fromY, `+${amount} ${emoji}`, {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '22px',
      fontStyle: '900',
      color,
      stroke: '#ffffff',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(200);

    this.tweens.add({
      targets: label,
      x: chipX,
      y: HUD_CHIP_Y,
      scaleX: 0.6,
      scaleY: 0.6,
      duration: 620,
      ease: 'Cubic.In',
      onComplete: () => {
        label.destroy();
        // Bounce the chip
        this.tweens.add({
          targets: chipText,
          scaleX: 1.4,
          scaleY: 1.4,
          duration: 100,
          yoyo: true,
          ease: 'Back.Out',
        });
      },
    });
  }

  private refresh(): void {
    const { stars, coins, gems } = GameState.data.currency;

    // Animate if currency increased — use standard pop if no fly event was sent
    if (stars > this.prevStars) {
      this._popGain(HUD_CHIP_X.stars, `+${stars - this.prevStars} ⭐`, '#ffd700');
    }
    if (coins > this.prevCoins) {
      this._popGain(HUD_CHIP_X.coins, `+${coins - this.prevCoins} 💰`, '#f0a010');
    }
    if (gems > this.prevGems) {
      this._popGain(HUD_CHIP_X.gems, `+${gems - this.prevGems} 💎`, '#9b6bff');
    }

    this.prevStars = stars;
    this.prevCoins = coins;
    this.prevGems  = gems;

    this.starsText.setText(stars.toLocaleString());
    this.coinsText.setText(coins.toLocaleString());
    this.gemsText.setText(gems.toLocaleString());
  }

  /** Floating +N label that rises and fades (fallback if no fly-from position) */
  private _popGain(x: number, text: string, color: string): void {
    const label = this.add.text(x, 32, text, {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '20px',
      fontStyle: '900',
      color,
    }).setOrigin(0.5).setDepth(100);

    this.tweens.add({
      targets: label,
      y: -20,
      alpha: 0,
      scaleX: 1.4,
      scaleY: 1.4,
      duration: 900,
      ease: 'Cubic.Out',
      onComplete: () => label.destroy(),
    });
  }
}
